import {
  Link,
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useId, type ReactNode } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import BackButton from '@/components/ui/BackButton'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import StatStrip from '@/components/ui/StatStrip'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import { useBasketBacktest, useSuspenseBasketDetail } from '@/features/baskets/api/queries'
import { basketArt } from '@/features/baskets/lib/art'
import { AUDIENCE_LABELS } from '@/features/baskets/lib/audience'
import { tagTint } from '@/features/baskets/lib/tags'
import SafeHtml from '@/features/courses/components/SafeHtml'
import { EM_DASH } from '@/lib/broker-format'
import { formatDate } from '@/lib/format'
import { revealMotion } from '@/lib/motion/reveal'
import { cn } from '@/lib/utils'
import type { BasketDetail } from '@/schemas/baskets'
import type { PageSize } from '@/schemas/table'
import {
  BASKET_DETAIL_SEARCH_DEFAULTS,
  BASKET_TABS,
  basketDetailSearchSchema,
  basketTabSchema,
  loadBasketDetail,
  type BasketTab,
} from '@/screens/baskets/BasketDetailScreen.loader'
import {
  BasketDetailSkeleton,
  BasketHeaderSkeleton,
} from '@/screens/baskets/components/BasketsSkeletons'
import HistoryTab from '@/screens/baskets/components/HistoryTab'
import InOutTab from '@/screens/baskets/components/InOutTab'
import MembersTab from '@/screens/baskets/components/MembersTab'
import PastResultsTab from '@/screens/baskets/components/PastResultsTab'

const BACK = '/baskets'

function BasketNotFound() {
  return (
    <ScreenLayout pageTitle="Basket not found">
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Basket not found"
        description="This basket may have been taken down, or the link is wrong."
        action={
          <Link to={BACK} className={stateActionClass('page')}>
            Back to baskets
          </Link>
        }
      />
    </ScreenLayout>
  )
}

function BasketDetailPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading this basket…"
      header={<BasketHeaderSkeleton />}
      pageTitle="Basket"
    >
      <BasketDetailSkeleton />
    </ScreenLayout>
  )
}

/** A genuine failure only: `BASKET_NOT_FOUND` resolves to `null` and never reaches here. */
function BasketDetailError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={
        <div className="flex h-9 items-center">
          <BackButton to={BACK} />
        </div>
      }
      title="This basket did not load"
    />
  )
}

export const Route = createFileRoute('/_app/baskets/$basketId')({
  validateSearch: basketDetailSearchSchema,
  search: { middlewares: [stripSearchParams(BASKET_DETAIL_SEARCH_DEFAULTS)] },
  loader: loadBasketDetail,
  pendingComponent: BasketDetailPending,
  errorComponent: BasketDetailError,
  component: BasketDetailRoute,
})

/** Keyed by the slug, so moving between baskets remounts the screen rather than carrying over. */
function BasketDetailRoute() {
  const slug = Route.useParams().basketId
  return <BasketDetailScreen key={slug} slug={slug} />
}

const TAB_LABELS: Record<BasketTab, string> = {
  members: 'Members',
  past: 'Past Results',
  how: 'How It’s Built',
  history: 'History',
  io: 'In or Out',
}

/**
 * The `how` tab is How It's Picked for a trader basket, whose rule says it in plain words, and
 * How It's Built for an investor basket, when staff wrote one: a tab with nothing behind it is not
 * drawn. History is a trader basket's alone: its members change after every close, where an
 * investor basket's change only when staff edit them. Past Results is drawn only for a backtest
 * with points: none yet (too little history, not built, or the endpoint not deployed) draws no tab.
 */
const tabsFor = (basket: BasketDetail, hasPast: boolean): TabBarOption[] =>
  BASKET_TABS.filter(
    (tab) =>
      (tab !== 'how' || basket.rule !== null || basket.howItsBuilt !== null) &&
      (tab !== 'history' || basket.audience === 'trader') &&
      (tab !== 'past' || hasPast),
  ).map((tab) => ({
    value: tab,
    label: tab === 'how' && basket.rule !== null ? 'How It’s Picked' : TAB_LABELS[tab],
  }))

/**
 * Over the title (mock `bkd-head__id`): the basket's first tag as its tinted chip, then who it is
 * for in the quieter grey, where the mock names the group.
 */
const kickerOf = (basket: BasketDetail): ReactNode => {
  const tag = basket.tags[0]
  const audience = basket.audience === null ? null : AUDIENCE_LABELS[basket.audience]
  if (tag === undefined && audience === null) return undefined
  return (
    <span className="flex flex-wrap items-center gap-2">
      {tag === undefined ? null : (
        <span
          style={tagTint(tag)}
          className="basket-tint bg-(--basket-chip-bg) px-2 text-body-xs font-semibold text-(--basket-chip-fg)"
        >
          {tag.name}
        </span>
      )}
      {audience}
    </span>
  )
}

/** The mock's round cover: the basket's picture, 44 on a phone and 52 from `sm`. */
function BasketCover({ art }: { art: string }) {
  return (
    <span className="flex size-11 items-center justify-center rounded-full bg-muted sm:size-13">
      <img src={art} alt="" className="size-7 object-contain sm:size-8" />
    </span>
  )
}

/** A trader basket's rule, in plain words, over anything staff also wrote about it. */
function HowPanel({ basket }: { basket: BasketDetail }) {
  return (
    <div className="flex flex-col gap-4 border border-border-subtle bg-card px-5 py-6 sm:px-8">
      {basket.rule === null ? null : (
        <p className="max-w-prose text-body-m text-foreground">{basket.rule.description}</p>
      )}
      {basket.howItsBuilt === null ? null : (
        <SafeHtml html={basket.howItsBuilt} headingLevel={2} className="max-w-prose" />
      )}
    </div>
  )
}

/**
 * Baskets › one basket (mock `BT`): its round picture beside its first tag and who it is for, the
 * name and the tagline; a strip of how many companies it holds (and a trader basket's rule) and
 * when its figures were read; then Members, Past Results (mm-be #375), How It's Built (How It's
 * Picked) and In or Out.
 *
 * Left out until a field feeds them: the group (ask B1), "Rebalanced" (B3) and "Money split"
 * (B4). History has no mock frame: the app's own table (owner, 2026-11-02).
 * Staff's How It's Built text is rendered as written, through the app's one sanitiser, not split
 * into the mock's "Step n" cards (owner Q8). No plan gate (owner Q7).
 */
function BasketDetailScreen({ slug }: { slug: string }) {
  const idBase = useId()
  const { data: basket } = useSuspenseBasketDetail(slug)
  const backtest = useBasketBacktest(slug)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  if (basket === null) return <BasketNotFound />

  const pastSeries = backtest.data?.series.length ?? 0
  const tabs = tabsFor(basket, pastSeries > 0)
  const art = basketArt(basket)
  const activeTab: BasketTab = tabs.some((tab) => tab.value === search.tab) ? search.tab : 'members'

  // A picked company belongs to In or Out alone: leaving the tab closes its verdict, as the mock
  // does, so coming back starts from the list. Members and History share the page, so a tab
  // change starts it again.
  const setTab = (value: string) => {
    const tab = basketTabSchema.parse(value)
    void navigate({
      search: (prev) => ({ ...prev, tab, s: tab === 'io' ? prev.s : undefined, page: 1 }),
    })
  }
  const setPage = (page: number) => {
    void navigate({ search: (prev) => ({ ...prev, page }) })
  }
  const setPageSize = (pageSize: PageSize) => {
    void navigate({ search: (prev) => ({ ...prev, pageSize, page: 1 }) })
  }

  const pick = (symbol: string | undefined) => {
    void navigate({ search: (prev) => ({ ...prev, tab: 'io', s: symbol }), replace: true })
  }

  return (
    <ScreenLayout
      header={
        <TitleRow
          title={basket.name}
          back={BACK}
          {...(basket.audience === 'trader' ? { backSearch: { audience: 'trader' } } : {})}
          leading={art === null ? undefined : <BasketCover art={art} />}
          eyebrow={kickerOf(basket)}
          subtitle={basket.tagline ?? undefined}
        />
      }
      pageTitle={basket.name}
    >
      <StatStrip
        items={[
          {
            label: 'Companies',
            value: basket.members.length === 0 ? 'None now' : basket.members.length,
          },
          ...(basket.rule === null ? [] : [{ label: 'Rule', value: basket.rule.label }]),
          {
            label: 'Data as of',
            value: basket.figuresAsOf === null ? EM_DASH : formatDate(basket.figuresAsOf),
          },
        ]}
      />

      <div className="flex flex-col gap-4">
        <TabBar
          options={tabs}
          value={activeTab}
          onChange={setTab}
          ariaLabel="Basket sections"
          idBase={idBase}
        />
        <TabPanel
          idBase={idBase}
          value={activeTab}
          className={cn('flex flex-col gap-6', revealMotion('fade-up').className)}
          key={activeTab}
        >
          {activeTab === 'members' ? (
            <MembersTab
              slug={slug}
              columns={basket.columns}
              members={basket.members}
              page={search.page}
              pageSize={search.pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onOpenInOut={() => {
                setTab('io')
              }}
            />
          ) : activeTab === 'past' && backtest.data != null ? (
            <PastResultsTab backtest={backtest.data} basketName={basket.name} />
          ) : activeTab === 'how' ? (
            <HowPanel basket={basket} />
          ) : activeTab === 'history' ? (
            <HistoryTab
              slug={slug}
              page={search.page}
              pageSize={search.pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          ) : (
            <InOutTab slug={slug} picked={search.s} onPick={pick} />
          )}
        </TabPanel>
      </div>
    </ScreenLayout>
  )
}
